import type { ModelCatalog } from "@cleo/contracts";

/**
 * The Neucleos models a person may choose, read from their World's own offer (modelTiers.catalog): the three tiers whose
 * published model is sold, usable and priced for them, then each other model the World sells, priced and usable for them,
 * beyond the Fireworks models the tiers serve. One rule for every chooser and for the World's own check of a choice: the
 * Desk's model chooser (apps/web/src/components/chat/modelChooserRoot.ts), Pocket's cloud conversations, and the World,
 * which accepts a cloud conversation's engine only when it is one of these (backend/convex/cleoConversations.js).
 *
 * An engine is a choice as a cloud conversation stores it: a tier's row, or a model on the World's managed connection for its
 * provider. An offer whose provider has no managed connection in the World has no engine: the Desk runs it through its own
 * platform, and a cloud conversation cannot. No member-owned subscription is ever one of these.
 */
export type ManagedTier = "fast" | "standard" | "deep";
export const MANAGED_TIER_LABELS: Readonly<Record<ManagedTier, string>> = {
  fast: "Fast",
  standard: "Standard",
  deep: "Deep",
};
const TIER_ORDER: ReadonlyArray<ManagedTier> = ["fast", "standard", "deep"];

export type ManagedEngine =
  | { readonly row: string }
  | { readonly model: string; readonly connectionKey: string };

type CatalogModel = ModelCatalog["models"][number];

export interface ManagedModelOffer {
  /** The chooser's key: `neucleos/<tier>` for a tier, `<provider>/<model>` for another model. */
  readonly slug: string;
  readonly section: "neucleos" | "frontier";
  readonly tier?: ManagedTier;
  readonly engine?: ManagedEngine;
  readonly model: CatalogModel;
}

const offered = (model: CatalogModel | undefined): model is CatalogModel =>
  model !== undefined && model.usable && model.sold && model.price !== null;

/** What the World offers this person now, in the chooser's order. Names and prices are `managedModelChoices`'. */
export function managedModelOffer(catalog: ModelCatalog | null): ReadonlyArray<ManagedModelOffer> {
  if (!catalog) return [];
  const offers: ManagedModelOffer[] = [];
  for (const tier of TIER_ORDER) {
    const published = catalog.tiers.find((item) => item.tier === tier);
    const model =
      published &&
      catalog.models.find(
        (item) => item.provider === published.provider && item.model === published.model,
      );
    if (published && offered(model))
      offers.push({
        slug: `neucleos/${tier}`,
        section: "neucleos",
        tier,
        engine: { row: published.row },
        model,
      });
  }
  for (const model of catalog.models) {
    if (model.provider === "fireworks" || !offered(model)) continue;
    const connectionKey = catalog.connections.find(
      (item) => item.provider === model.provider,
    )?.connectionKey;
    offers.push({
      slug: `${model.provider}/${model.model}`,
      section: "frontier",
      ...(connectionKey ? { engine: { model: model.model, connectionKey } } : {}),
      model,
    });
  }
  return offers;
}

export function sameEngine(a: ManagedEngine | undefined, b: ManagedEngine | undefined): boolean {
  if (!a || !b) return false;
  if ("row" in a || "row" in b) return "row" in a && "row" in b && a.row === b.row;
  return a.model === b.model && a.connectionKey === b.connectionKey;
}

/** The price line the Desk's chooser shows: input and output per million tokens. */
export function managedCostLabel(model: CatalogModel): string {
  const price = model.price;
  if (
    !price ||
    price.inputRateMicrosPerMillion === null ||
    price.outputRateMicrosPerMillion === null
  )
    return "Price unavailable";
  const format = (micros: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: price.currency,
      maximumFractionDigits: 3,
    }).format(micros / 1_000_000);
  return `${format(price.inputRateMicrosPerMillion)} in / ${format(price.outputRateMicrosPerMillion)} out · 1M`;
}

export interface ManagedModelChoice extends ManagedModelOffer {
  readonly label: string;
  readonly cost: string;
  /** Whether the model reads photos and PDF pages; absent when the World does not say. */
  readonly imageInput?: boolean;
  /** Why it may not run just now (credits, budget, the provider), in the World's words. */
  readonly readiness?: string;
}

/** Whether the World offers this engine to this person now (a cloud conversation's choice, checked where it is stored). */
export function managedEngineOffered(catalog: ModelCatalog | null, engine: ManagedEngine): boolean {
  return managedModelOffer(catalog).some((offer) => sameEngine(offer.engine, engine));
}

/** The offer as the person sees it: the Desk chooser's names and price lines. */
export function managedModelChoices(
  catalog: ModelCatalog | null,
): ReadonlyArray<ManagedModelChoice> {
  return managedModelOffer(catalog).map((offer) => ({
    ...offer,
    label: offer.tier ? MANAGED_TIER_LABELS[offer.tier] : offer.model.model,
    cost: managedCostLabel(offer.model),
    ...(offer.model.imageInput === undefined ? {} : { imageInput: offer.model.imageInput }),
    ...(offer.model.readiness?.blockers.length
      ? { readiness: offer.model.readiness.blockers.map((blocker) => blocker.message).join(" · ") }
      : {}),
  }));
}
